/**
 * React Query Provider
 *
 * Configures and provides the QueryClient for the application.
 * Sets up default options for queries and mutations.
 *
 * @module contexts/QueryProvider
 */

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

// ============================================
// Query Client Configuration
// ============================================

/**
 * Create a QueryClient with optimized defaults for our use case
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Data is considered fresh for 30 seconds
      staleTime: 30 * 1000,
      // Cache data for 5 minutes
      gcTime: 5 * 60 * 1000,
      // Retry failed requests up to 2 times
      retry: 2,
      // Don't retry on 4xx errors
      retryOnMount: true,
      // Refetch on window focus for fresh data
      refetchOnWindowFocus: true,
      // Don't refetch on reconnect by default
      refetchOnReconnect: 'always',
      // Network mode - always try to fetch
      networkMode: 'always'
    },
    mutations: {
      // Retry mutations once on failure
      retry: 1,
      networkMode: 'always'
    }
  }
});

// ============================================
// Provider Component
// ============================================

/**
 * QueryProvider Component
 *
 * Wraps the application with QueryClientProvider.
 *
 * @param {object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 * @returns {React.ReactElement} Provider element
 */
export function QueryProvider({ children }) {
  return (
    <QueryClientProvider client={queryClient}>
      {children}
    </QueryClientProvider>
  );
}

/**
 * Export the queryClient for manual cache manipulation
 * (e.g., prefetching, invalidation outside of components)
 */
export { queryClient };

export default QueryProvider;
